import type { DrawMode, LatLngTuple } from "./options";

export type UrlState = {
  maps: string[] | null;
  layers: string[] | null;
  on: string[] | null;
  zoom: number | null;
  center: LatLngTuple | null;
  objectIds: number[];
  feature: { layer: string; id: string } | null;
  draw: DrawMode | null;
  wkt: string | null;
  query: string | null;
  lang: "hu" | "en" | null;
  theme: "light" | "dark" | "system" | null;
  chrome: "full" | "compact" | "none" | null;
  panel: "layers" | "info" | "search" | "legend" | null;
  tab: string | null;
};

export const urlKeys = ["maps", "layers", "on", "z", "lat", "lon", "obj", "feat", "draw", "wkt", "q", "lang", "theme", "chrome", "panel", "tab"] as const;

const drawModes = new Set<string>(["point", "polyline", "polygon"]);
const themes = new Set<string>(["light", "dark", "system"]);
const chromes = new Set<string>(["full", "compact", "none"]);
const panels = new Set<string>(["layers", "info", "search", "legend"]);
const langs = new Set<string>(["hu", "en"]);

export function emptyUrlState(): UrlState {
  return {
    maps: null,
    layers: null,
    on: null,
    zoom: null,
    center: null,
    objectIds: [],
    feature: null,
    draw: null,
    wkt: null,
    query: null,
    lang: null,
    theme: null,
    chrome: null,
    panel: null,
    tab: null
  };
}

function list(value: string | null): string[] | null {
  if (value === null) return null;

  return value
    .split(",")
    .map(a => a.trim())
    .filter(a => a.length > 0);
}

function number(value: string | null, min: number, max: number): number | null {
  if (value === null || value.trim().length === 0) return null;

  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed >= min && parsed <= max ? parsed : null;
}

export function decode(search: string): UrlState {
  const params = new URLSearchParams(search.startsWith("?") ? search.substring(1) : search);
  const state = emptyUrlState();

  state.maps = list(params.get("maps"));
  state.layers = list(params.get("layers"));
  state.on = list(params.get("on"));
  if (state.on !== null && state.on.length === 0) state.on = null;
  state.zoom = number(params.get("z"), 0, 24);

  const lat = number(params.get("lat"), -90, 90);
  const lon = number(params.get("lon"), -180, 180);

  state.center = lat !== null && lon !== null ? [lat, lon] : null;
  state.objectIds = (list(params.get("obj")) ?? []).map(Number).filter(a => Number.isInteger(a) && a > 0);

  const feat = params.get("feat");

  if (feat && feat.includes(":")) {
    const index = feat.indexOf(":");
    const layer = feat.substring(0, index);
    const id = feat.substring(index + 1);

    state.feature = layer.length > 0 && id.length > 0 ? { layer, id } : null;
  }

  const draw = params.get("draw");

  state.draw = draw && drawModes.has(draw) ? (draw as DrawMode) : null;
  state.wkt = params.get("wkt");
  state.query = params.get("q");

  const lang = params.get("lang");
  const theme = params.get("theme");
  const chrome = params.get("chrome");
  const panel = params.get("panel");

  state.lang = lang && langs.has(lang) ? (lang as "hu" | "en") : null;
  state.theme = theme && themes.has(theme) ? (theme as UrlState["theme"]) : null;
  state.chrome = chrome && chromes.has(chrome) ? (chrome as UrlState["chrome"]) : null;
  state.panel = panel && panels.has(panel) ? (panel as UrlState["panel"]) : null;
  state.tab = params.get("tab");

  return state;
}

export function encode(state: Partial<UrlState>, base = ""): string {
  const params = new URLSearchParams(base.startsWith("?") ? base.substring(1) : base);

  for (const key of urlKeys) params.delete(key);

  if (state.maps && state.maps.length > 0) params.set("maps", state.maps.join(","));
  if (state.layers && state.layers.length > 0) params.set("layers", state.layers.join(","));
  if (state.on && state.on.length > 0) params.set("on", state.on.join(","));
  if (state.zoom !== null && state.zoom !== undefined) params.set("z", String(Math.round(state.zoom * 100) / 100));
  if (state.center) {
    params.set("lat", state.center[0].toFixed(5));
    params.set("lon", state.center[1].toFixed(5));
  }
  if (state.objectIds && state.objectIds.length > 0) params.set("obj", state.objectIds.join(","));
  if (state.feature) params.set("feat", `${state.feature.layer}:${state.feature.id}`);
  if (state.draw) params.set("draw", state.draw);
  if (state.wkt) params.set("wkt", state.wkt);
  if (state.query) params.set("q", state.query);
  if (state.lang) params.set("lang", state.lang);
  if (state.theme) params.set("theme", state.theme);
  if (state.chrome) params.set("chrome", state.chrome);
  if (state.panel) params.set("panel", state.panel);
  if (state.tab) params.set("tab", state.tab);

  const text = params.toString();

  return text.length > 0 ? `?${text}` : "";
}

export function sameUrlState(a: UrlState, b: UrlState): boolean {
  return encode(a) === encode(b);
}
